import { useState, useMemo } from 'react';
import type { Order } from '../../types/ordering.types';
import { OrderStatus } from '../../types/ordering.types';
import {
  OrderListTable,
  COL_REF, COL_DATE, COL_DESC, COL_SUPPLIER, COL_EXP_DELIVERY, COL_TOTAL, COL_STATUS, COL_REQUESTER,
} from './OrderListTable';

type ChemFilter = 'all' | 'pending_chem' | 'approved' | 'ordered' | 'delivered';

const FILTERS: { key: ChemFilter; label: string }[] = [
  { key: 'all',          label: 'All' },
  { key: 'pending_chem', label: 'Awaiting Chemical Approval' },
  { key: 'approved',     label: 'Chemical Approved' },
  { key: 'ordered',      label: 'Ordered' },
  { key: 'delivered',    label: 'Delivered' },
];

interface Props {
  orders: Order[];
  isLoading: boolean;
  onOpenOrder: (order: Order) => void;
  search: string;
}

export function ChemicalOrders({ orders, isLoading, onOpenOrder, search }: Props) {
  const [filter, setFilter] = useState<ChemFilter>('all');

  const filtered = useMemo(() => {
    let list = orders;
    if (filter === 'pending_chem') list = list.filter(o => o.status === OrderStatus.PendingChemApproval);
    else if (filter === 'approved') list = list.filter(o => o.status === OrderStatus.ChemicalApproved);
    else if (filter === 'ordered')  list = list.filter(o => o.status === OrderStatus.Ordered || o.status === OrderStatus.PartialDelivery);
    else if (filter === 'delivered') list = list.filter(o => o.status === OrderStatus.Delivered);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(o =>
        o.title.toLowerCase().includes(q) ||
        o.orderRef.toLowerCase().includes(q) ||
        o.requesterName.toLowerCase().includes(q) ||
        o.supplier.toLowerCase().includes(q)
      );
    }
    return list;
  }, [orders, filter, search]);

  const pendingCount = orders.filter(o => o.status === OrderStatus.PendingChemApproval).length;

  const columns = [
    COL_REF(), COL_DATE('orderDate', 'ORDERED'), COL_DESC(), COL_SUPPLIER(),
    COL_EXP_DELIVERY(), COL_TOTAL(), COL_STATUS(), COL_REQUESTER(),
  ];

  return (
    <div className="ord-view-content">
      <div className="info-box blue">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M9 3h6v7l3 4H6l3-4V3z" /><line x1="9" y1="17" x2="15" y2="17" /><line x1="12" y1="17" x2="12" y2="21" />
        </svg>
        <span>
          <strong>Chemical Orders</strong> — orders containing chemical line items.
          {pendingCount > 0
            ? ` ${pendingCount} order${pendingCount > 1 ? 's' : ''} awaiting your chemical approval.`
            : ' No orders currently awaiting chemical approval.'}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
        {FILTERS.map(f => (
          <button
            key={f.key}
            className={`filter-tab${filter === f.key ? ' active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
            {f.key === 'pending_chem' && pendingCount > 0 && (
              <span style={{ opacity: 0.7 }}> ({pendingCount})</span>
            )}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="ord-empty"><div className="spinner" /> Loading orders…</div>
      ) : (
        <OrderListTable
          orders={filtered}
          columns={columns}
          onRowClick={onOpenOrder}
          emptyMessage="No chemical orders match the current filter."
        />
      )}
    </div>
  );
}
